import { useState } from "react";
import {
  ActionBar,
  Badge,
  Button,
  FormattedDate,
  IdentityBadge,
  Skeleton,
  TextArea,
} from "@core/components";
import { cn } from "@core/utils/cn";
import { getInitials } from "@core/utils/initials/initials";
import {
  renderDocumentStatus,
  renderDocumentStatusText,
} from "@core/utils/documents/renderDocumentStatus";
import {
  useGetApprovalInfo,
  useApproveApproval,
} from "@modules/docs/annotations/hooks";
import { FEATURE } from "@modules/subscription";
import { RejectApproval } from "./RejectApproval";

export type ApprovalInfoProps = {
  onClose: VoidFunction;
  canAnnotate: boolean;
};

export function ApprovalInfo({ onClose, canAnnotate }: ApprovalInfoProps) {
  const { data, isLoading } = useGetApprovalInfo();

  const approveApprovalMutation = useApproveApproval();
  const [isRejectOpen, setIsRejectOpen] = useState(false);

  const canDecide = canAnnotate && data?.status === "in_review";

  const submitterName = data?.submitter
    ? `${data.submitter.first_name} ${data.submitter.last_name}`.trim()
    : "";
  const reviewerName = data?.reviewer
    ? `${data.reviewer.first_name} ${data.reviewer.last_name}`.trim()
    : "";

  return (
    <>
      <ActionBar title="Approval Details" onClose={onClose} editable={false}>
        {isLoading ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          <div className="flex flex-col justify-between h-full">
            {/* Document name */}
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-neutral-400 uppercase tracking-wide">
                  Document
                </span>
                <span className="text-sm font-medium text-neutral-900 truncate">
                  {data?.doc?.name ?? "—"}
                </span>
              </div>

              {/* Status */}
              <div className="flex flex-col gap-1">
                <span className="text-xs text-neutral-400 uppercase tracking-wide">
                  Status
                </span>
                {data?.status ? (
                  <Badge
                    title={renderDocumentStatusText(data.status)}
                    className={cn(
                      renderDocumentStatus(data.status),
                      "w-[5.3rem] h-[2rem] flex items-center justify-center font-medium",
                    )}
                  />
                ) : (
                  <span className="text-sm text-neutral-400">—</span>
                )}
              </div>

              {/* Due date */}
              {data?.due_date && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-neutral-400 uppercase tracking-wide">
                    Due date
                  </span>
                  <FormattedDate date={data.due_date} displayType="details" />
                </div>
              )}

              {/* Submitter */}
              {data?.submitter && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-neutral-400 uppercase tracking-wide">
                    Submitted by
                  </span>
                  <IdentityBadge
                    mediaType="picture"
                    media={data.submitter.picture || ""}
                    initials={getInitials(submitterName)}
                    title={submitterName}
                    description={data.submitter.email}
                  />
                </div>
              )}

              {/* Reviewer */}
              {data?.reviewer && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-neutral-400 uppercase tracking-wide">
                    Reviewer
                  </span>
                  <IdentityBadge
                    mediaType="picture"
                    media={data.reviewer.picture || ""}
                    initials={getInitials(reviewerName)}
                    title={reviewerName}
                    description={data.reviewer.email}
                  />
                </div>
              )}

              {/* Description */}
              {data?.description && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-neutral-400 uppercase tracking-wide">
                    Description
                  </span>
                  <TextArea
                    value={data.description}
                    readOnly
                    rows={3}
                    className="resize-none"
                  />
                </div>
              )}

              {/* Rejection reasons */}
              {(data?.rejection_reasons?.length ?? 0) > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-xs text-neutral-400 uppercase tracking-wide">
                    Rejection reasons
                  </span>
                  <ul className="flex flex-col gap-1.5">
                    {data!.rejection_reasons?.map((reason, i) => (
                      <li
                        key={i}
                        className="text-sm text-accent-red-700 bg-accent-red-50 rounded px-2 py-1"
                      >
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Actions */}
            {data?.status === "in_review" && (
              <div className="flex items-center gap-2 pt-2">
                <FEATURE has="write:allowed">
                  <Button
                    type="button"
                    variant="solid"
                    size="sm"
                    className="flex-1"
                    isLoading={approveApprovalMutation.isPending}
                    disabled={!canDecide || approveApprovalMutation.isPending}
                    onClick={() => approveApprovalMutation.mutate()}
                  >
                    Approve
                  </Button>
                </FEATURE>
                <FEATURE has="write:allowed">
                  <Button
                    type="button"
                    size="sm"
                    className="flex-1 bg-accent-red-700 border-accent-red-800"
                    disabled={!canDecide || approveApprovalMutation.isPending}
                    onClick={() => setIsRejectOpen(true)}
                  >
                    Reject
                  </Button>
                </FEATURE>
              </div>
            )}
          </div>
        )}
      </ActionBar>

      {isRejectOpen && (
        <RejectApproval onClose={() => setIsRejectOpen(false)} />
      )}
    </>
  );
}
